import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import { Delete as TrashIcon } from '@mui/icons-material';
import {
    TextInput,
    DateInput,
    NumberInput,
    Text,
    Theme,
    Button,
    Modal,
    Services
} from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';
import Grid from "@mui/material/Grid";
import { submitMultiItemClaim, getClaim } from '../../../actions/adjudication.js';

class MultiItemForm extends PureComponent {
    state = {
        showConfirmModal: false,
        claims: {}
    };

    _handleChange = (type, value, index) => {
        this.setState({
            claims: {
                ...this.state.claims,
                [index]: {
                    ...this.state.claims[index],
                    [type]: value
                }
            }
        });
    };

    _addClaim = () => {
        const numClaims = Object.keys(this.state.claims).length;

        this.setState({
            claims: {
                ...this.state.claims,
                [numClaims]: {
                    notes: null,
                    amountSubmitted: null,
                    amountPaid: null,
                    adjudicatedAmount: null,
                    category: null,
                    service: null,
                    serviceDate: null,
                    provider: null,
                    deductible: null,
                    reimbursement: null,
                    hsaAmount: null,
                    description: null,
                    amountSubmittedToFirstPayor: null,
                    totalPaidByFirstPayor: null,
                    amountNotCoveredByFirstPayor: null,
                    status: null
                }
            }
        });
    };

    _toggleConfirmModal = async () => {
        if (!this.state.showConfirmModal) {
            const isValid = await this._validateForm();

            if (!isValid) {
                return false;
            }
        }

        this.setState({
            showConfirmModal: !this.state.showConfirmModal
        });

        return true;
    };

    _parseData = () => Object.keys(this.state.claims).map((index) => {
        const item = this.state.claims[index];

        // set the correct hsaAmount, on when it's a standard claim, can the value get changed.
        let hsaAmount = Number(item.hsaAmount);
        const isHsaClaim = this.props.defaults.type.toLowerCase() === 'hsa';
        const isLsaClaim = this.props.defaults.type.toLowerCase() === 'lsa';

        if (isHsaClaim || isLsaClaim || !this.props.showHsaConversionAndTopOff) {
            hsaAmount = 0;
        }

        return {
            ...item,
            notes: !item.notes || item.notes.length === 0 ? null : item.notes,
            amountSubmitted: Number(item.amountSubmitted),
            amountPaid: Number(item.amountPaid),
            adjudicatedAmount: Number(item.adjudicatedAmount),
            deductible: Number(item.deductible),
            reimbursement: Number(item.reimbursement),
            hsaAmount: hsaAmount,
            amountSubmittedToFirstPayor: Number(item.amountSubmittedToFirstPayor),
            totalPaidByFirstPayor: Number(item.totalPaidByFirstPayor),
            amountNotCoveredByFirstPayor: Number(item.amountNotCoveredByFirstPayor),
            serviceDate: dates.getUTC(item.serviceDate)
        };
    });

    _submit = async () => {
        const claims = await this._parseData();

        this.props?.onAdjudicationStart();
        this.setState({ showConfirmModal: false });

        const success = await this.props.dispatch(submitMultiItemClaim(this.props.claimID, {
            claims,
            claimFormat: this.props.claimFormat
        }));

        if (!success) {
            return false;
        }

        await this.props.dispatch(getClaim(this.props.claimID));

        return true;
    };

    _validateForm = async () => {
        let hasErrors = false;

        if (Object.keys(this.state.claims).length === 0) {
            alert('You must add at least one claim item to submit a multi item claim.');
            return false;
        }

        const promises = Object.keys(this.state.claims).map(async (i) => {
            const item = this.state.claims[i];

            const amountSubmittedError = await Services.validateInput('checkEmpty', item.amountSubmitted || '');
            const adjudicatedAmountError = await Services.validateInput('checkEmpty', item.adjudicatedAmount || '');
            const amountPaidError = await Services.validateInput('checkEmpty', item.amountPaid || '');
            const categoryError = await Services.validateInput('checkEmpty', item.category || '');
            const serviceError = await Services.validateInput('checkEmpty', item.service || '');
            const serviceDateError = await Services.validateInput('date', item.serviceDate);
            const providerError = await Services.validateInput('checkEmpty', item.provider || '');
            const deductibleError = await Services.validateInput('checkEmpty', item.deductible || '');
            const reimbursementError = await Services.validateInput('checkEmpty', item.reimbursement || '');
            const statusError = await Services.validateInput('checkEmpty', item.status || '');
            const hsaAmountError = this.props.defaults.type.toLowerCase() === 'standard' && this.props.showHsaConversionAndTopOff ? // do not attempt to validate if the hsa field is hidden to agents
                await Services.validateInput('checkEmpty', item.hsaAmount || '')
                :
                null;

            let descriptionError = false;
            let amountSubmittedToFirstPayorError = false;
            let totalPaidByFirstPayorError = false;
            let amountNotCoveredByFirstPayorError = false;

            if (this.props.claimFormat === 'cob') {
                // check the fields that are required for cob
                descriptionError = await Services.validateInput('checkEmpty', item.description || '');
                amountSubmittedToFirstPayorError = await Services.validateInput('checkEmpty', item.amountSubmittedToFirstPayor || '');
                totalPaidByFirstPayorError = await Services.validateInput('checkEmpty', item.totalPaidByFirstPayor || '');
                amountNotCoveredByFirstPayorError = await Services.validateInput('checkEmpty', item.amountNotCoveredByFirstPayor || '');
            }

            this.setState({
                [`amountSubmittedError${ i }`]: amountSubmittedError,
                [`adjudicatedAmountError${ i }`]: adjudicatedAmountError,
                [`amountPaidError${ i }`]: amountPaidError,
                [`serviceError${ i }`]: serviceError,
                [`categoryError${ i }`]: categoryError,
                [`serviceDateError${ i }`]: serviceDateError,
                [`providerError${ i }`]: providerError,
                [`hsaAmountError${ i }`]: hsaAmountError,
                [`deductibleError${ i }`]: deductibleError,
                [`reimbursementError${ i }`]: reimbursementError,
                [`descriptionError${ i }`]: descriptionError,
                [`amountSubmittedToFirstPayorError${ i }`]: amountSubmittedToFirstPayorError,
                [`totalPaidByFirstPayorError${ i }`]: totalPaidByFirstPayorError,
                [`amountNotCoveredByFirstPayorError${ i }`]: amountNotCoveredByFirstPayorError,
                [`statusError${ i }`]: statusError
            });

            if (
                amountSubmittedError ||
                adjudicatedAmountError ||
                amountPaidError ||
                serviceError ||
                serviceError ||
                serviceDateError ||
                providerError ||
                hsaAmountError ||
                deductibleError ||
                reimbursementError ||
                descriptionError ||
                amountSubmittedToFirstPayorError ||
                totalPaidByFirstPayorError ||
                amountNotCoveredByFirstPayorError ||
                statusError
            ) {
                hasErrors = true;
            }

            return null;
        });

        await Promise.all(promises);

        return !hasErrors;
    };

    _selectCategory = async (event, index) => {
        await this.setState({
            claims: {
                ...this.state.claims,
                [index]: {
                    ...this.state.claims[index],
                    category: event.target.value
                }
            }
        });
    };

    _selectService = (event, index, isValue) => {
        const value = isValue ? event : event.target.value;

        this.setState({
            claims: {
                ...this.state.claims,
                [index]: {
                    ...this.state.claims[index],
                    service: value
                }
            }
        });
    };

    _removeClaim = (index) => {
        delete this.state.claims[index];

        this.setState({
            claims: {
                ...this.state.claims
            }
        });
    };

    _claimItem = (index) => {
        const item = this.state.claims[index];
        const notes = item.notes instanceof Array ? item.notes.join('\n') : item.notes;

        let categoryOptions = [
            'Drugs',
            'Dental',
            'Health',
            'Paramedicals',
            'Vision',
            'Lifestyle Spending'
        ];

        // set the service options based on the category;
        let serviceOptions = [];
        if (!item.category) {
            serviceOptions = this.props.type === 'Lifestyle' ? [ 'Recreation', 'Supplements', 'Active Wear', 'Gym Pass' ] : this.props.claimTypes.Paramedicals.reverse();
        } else {
            serviceOptions = this.props.type === 'Lifestyle' ? [ 'Recreation', 'Supplements', 'Active Wear', 'Gym Pass' ] : this.props.claimTypes[item.category].reverse();
        }

        return (
            <div
                style={{ paddingBottom: 20, borderBottom: `1px solid #eee` }}
                key={index}
            >
                <Grid
                    container
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                >
                    <Text
                        mediumLarge
                        display="block"
                        margin="0"
                        color={ Theme.colors.skyBlue }
                    >
                        { `Claim ${ Number(index) + 1 }` }
                    </Text>
                    <IconButton
                        aria-label="delete"
                        key={index}
                        onClick={ () => this._removeClaim(index) }
                        sx={{ color: Theme.colors.mainTextGrey }}
                    >
                        <TrashIcon style={{ color: '#414042' }} />
                    </IconButton>
                </Grid>

                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    spacing={2}
                >
                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Amount Submitted"
                            fullWidth
                            prefix="$"
                            value={ item.amountSubmitted || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("amountSubmitted", e.target.value, index)}
                            error={ this.state[`amountSubmittedError${ index }`] }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Adjudicated Amount"
                            fullWidth
                            prefix="$"
                            value={ item.adjudicatedAmount || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("adjudicatedAmount", e.target.value, index)}
                            error={ this.state[`adjudicatedAmountError${ index }`] }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Amount Paid"
                            fullWidth
                            prefix="$"
                            value={ item.amountPaid || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("amountPaid", e.target.value, index)}
                            error={ this.state[`amountPaidError${ index }`] }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Category"
                            fullWidth
                            value={ item.category || '' }
                            onChange={(e) => this._selectCategory(e, index)}
                            variant="outlined"
                            error={ this.state.categoryError }
                            select
                            required
                            selectOptions={ categoryOptions }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Service"
                            variant="outlined"
                            value={ item.service || '' }
                            onChange={(e) => this._selectService(e, index)}
                            select
                            required
                            fullWidth
                            selectOptions={ serviceOptions }
                            error={ this.state.serviceError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <DateInput
                            label="Service Date"
                            fullWidth
                            value={ item.serviceDate || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("serviceDate", e.target.value, index)}
                            error={ this.state[`serviceDateError${ index }`] }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Coinsurance"
                            fullWidth
                            suffix="%"
                            value={ item.reimbursement || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("reimbursement", e.target.value, index)}
                            error={ this.state[`reimbursementError${ index }`] }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Provider"
                            fullWidth
                            value={ item.provider || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("provider", e.target.value, index)}
                            error={ this.state[`providerError${ index }`] }
                        />
                    </Grid>

                    {
                        this.props.defaults.type.toLowerCase() === 'standard' && this.props.showHsaConversionAndTopOff &&
                            <Grid item xs={12} sm={4}>
                                <NumberInput
                                    label="HSA Used"
                                    fullWidth
                                    prefix="$"
                                    value={ item.hsaAmount || '' }
                                    variant="outlined"
                                    onChange={(e) => this._handleChange("hsaAmount", e.target.value, index)}
                                    error={ this.state[`hsaAmountError${ index }`] }
                                />
                            </Grid>
                    }

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Deductible"
                            fullWidth
                            prefix="$"
                            value={ item.deductible || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("deductible", e.target.value, index)}
                            error={ this.state[`deductibleError${ index }`] }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Description"
                            fullWidth
                            value={ item.description || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("description", e.target.value, index)}
                            error={ this.state[`descriptionError${ index }`] }
                        />
                    </Grid>

                    {
                        (this.props.claimFormat === 'cob') &&
                            <React.Fragment>
                                <Grid item xs={12} sm={4}>
                                    <NumberInput
                                        label="Amount Submitted to First Payor"
                                        fullWidth
                                        prefix="$"
                                        value={ item.amountSubmittedToFirstPayor || '' }
                                        variant="outlined"
                                        onChange={(e) => this._handleChange("amountSubmittedToFirstPayor", e.target.value, index)}
                                        error={ this.state[`amountSubmittedToFirstPayorError${ index }`] }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <NumberInput
                                        label="Total Paid by First Payor"
                                        fullWidth
                                        prefix="$"
                                        value={ item.totalPaidByFirstPayor || '' }
                                        variant="outlined"
                                        onChange={(e) => this._handleChange("totalPaidByFirstPayor", e.target.value, index)}
                                        error={ this.state[`totalPaidByFirstPayorError${ index }`] }

                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <NumberInput
                                        label="Amount Not Covered by First Payor"
                                        fullWidth
                                        prefix="$"
                                        value={ item.amountNotCoveredByFirstPayor || '' }
                                        variant="outlined"
                                        onChange={(e) => this._handleChange("amountNotCoveredByFirstPayor", e.target.value, index)}
                                        error={ this.state[`amountNotCoveredByFirstPayorError${ index }`] }
                                    />
                                </Grid>
                            </React.Fragment>
                    }

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Status"
                            fullWidth
                            variant="outlined"
                            value={ item.status || '' }
                            onChange={(e) => this._handleChange("status", e.target.value, index)}
                            error={ this.state[`statusError${ index }`] }
                            select
                            selectOptions={[ 'Approved', 'Declined' ]}
                        />
                    </Grid>
                </Grid>

                <Text
                    medium
                    display="block"
                    margin="20px 0 0 0"
                    color={ Theme.colors.skyBlue }
                >
                    Notes
                </Text>

                <TextInput
                    fullWidth
                    value={ notes || '' }
                    variant="outlined"
                    onChange={(e) => this._handleChange("notes", e.target.value, index)}
                    multiline={ true }
                    rows={ 5 }
                />
            </div>
        );
    };

    render() {
        const styles = {
            btnBlock: {
                marginTop: 20,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center'
            },
            btnBlockLeft: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                marginTop: 10
            }
        };

        return (
            <div>
                <Text small>For each item on the claim receipt add a claim item. Each item will be converted to it's own claim.</Text>

                <div style={ styles.btnBlockLeft }>
                    <Button onClick={ this._addClaim } transparent disabled={this.props.claimsBlocked}>
                        Add Claim Item
                    </Button>
                </div>

                {
                    Object.keys(this.state.claims).map((index) => this._claimItem(index))
                }

                {
                    Object.keys(this.state.claims).length > 0 &&
                        <div style={ styles.btnBlock }>
                            <Button
                                onClick={ this._toggleConfirmModal }
                                color="primary"
                                disabled={this.props.claimsBlocked}
                            >
                                Submit
                            </Button>
                        </div>
                }

                <Modal
                    open={this.state.showConfirmModal}
                    onClose={ this._toggleConfirmModal }
                    title="Are you sure?"
                    primaryButton={{
                        label: "Continue",
                        onClick: this._submit 
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        { `Are you sure you want to submit these claims? Each line item will become it's own claim. This action is final and cannot be undone.` }
                    </Text>
                </Modal>
            </div>
        );
    }
}

export default connect()(MultiItemForm);